<template>
    <div
        v-if="showProgressBar"
        class="flex min-w-0"
        :class="labelPlacement === 'inline' ? 'items-center gap-2' : 'flex-col gap-2'"
    >
        <!-- split: "$500 spent" left, "$1,500 to go in 20 days" right (card
             details, MY1-3042). The remaining side stays empty once the goal
             is met and for the frozen deadline_passed bar. -->
        <div
            v-if="labelPlacement === 'split'"
            class="flex items-center justify-between gap-2"
        >
            <span
                class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 whitespace-nowrap"
            >
                {{ spentLabel }}
            </span>
            <span
                v-if="!isDisabled && remainingLabel"
                class="text-sm leading-5 text-slate-500 dark:text-slate-400 truncate"
            >
                {{ remainingLabel }}
            </span>
        </div>

        <span v-if="hasVisibleLabel && labelPlacement === 'above'" :class="labelClass">
            <span :class="spentClass">{{ compactParts.spent }}</span>
            <template v-if="compactParts.of">
                {{ ' ' }}<span :class="secondaryClass">{{ compactParts.of }} {{ compactParts.target }}</span>
            </template>
            <template v-if="compactParts.suffix">
                {{ ' ' }}<span :class="secondaryClass">{{ compactParts.suffix }}</span>
            </template>
        </span>

        <div
            class="relative h-1 w-full overflow-hidden rounded-full"
            :class="trackClass"
            role="progressbar"
            :aria-valuenow="Math.round(percent)"
            aria-valuemin="0"
            aria-valuemax="100"
            :aria-label="verboseLabel"
        >
            <div
                v-if="!isEmpty && percent > 0"
                class="absolute inset-y-0 left-0 rounded-full transition-all"
                :class="fillClass"
                :style="{ width: percent + '%' }"
            ></div>
            <!-- Pending (unposted) spend: a lighter segment after the posted
                 fill. Display-only — it never counts toward the threshold. -->
            <div
                v-if="isActive && pendingPercent > 0"
                class="absolute inset-y-0 bg-10x-orange/40"
                :class="{ 'rounded-full': percent <= 0 }"
                :style="{ left: percent + '%', width: pendingPercent + '%' }"
            ></div>
        </div>

        <span
            v-if="hasVisibleLabel && labelPlacement !== 'above' && labelPlacement !== 'split'"
            :class="labelClass"
        >
            <span :class="spentClass">{{ compactParts.spent }}</span>
            <template v-if="compactParts.of">
                {{ ' ' }}<span :class="secondaryClass">{{ compactParts.of }} {{ compactParts.target }}</span>
            </template>
            <template v-if="compactParts.suffix">
                {{ ' ' }}<span :class="secondaryClass">{{ compactParts.suffix }}</span>
            </template>
        </span>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { useBonusTracking } from '@/composables/useBonusTracking';

/**
 * MY1-3040 — the shared welcome-bonus progress bar. One component, three
 * variants, rendered identically on the wallet table, card details and the
 * dashboard min-spend widget:
 *
 *  - active   (tracking / goal_reached): brand track + fill
 *  - empty    (connectable / spending_disabled): flat gray track, no fill
 *  - disabled (deadline_passed): gray track, slate fill frozen at its value
 *
 * States with no bar at all (received, goal_not_met, suppressed,
 * not_auto_trackable — or a payload without a tracking object) render
 * nothing; the surface's status pill carries the story.
 */
const props = defineProps({
    bonus: {
        type: Object,
        required: true,
    },
    // Where the "$500 of $2,000" label sits relative to the bar. 'split'
    // renders the card-details form instead: "$X spent" left and the
    // "$Y to go in N days" remainder right, above the bar.
    labelPlacement: {
        type: String,
        default: 'below',
        validator: (value) =>
            ['above', 'below', 'inline', 'hidden', 'split'].includes(value),
    },
});

const {
    showProgressBar,
    isActive,
    isDisabled,
    isEmpty,
    percent,
    pendingPercent,
    compactParts,
    verboseLabel,
    spentLabel,
    remainingLabel,
} = useBonusTracking(() => props.bonus);

const hasVisibleLabel = computed(() => props.labelPlacement !== 'hidden');

const trackClass = computed(() =>
    isActive.value
        ? 'bg-10x-orange-200 dark:bg-10x-orange-200/25'
        : 'bg-slate-200 dark:bg-slate-600'
);

const fillClass = computed(() =>
    isDisabled.value ? 'bg-slate-500 dark:bg-slate-400' : 'bg-10x-orange'
);

const labelClass = 'text-sm leading-5 whitespace-nowrap';
const spentClass = 'font-normal text-slate-800 dark:text-slate-100';
const secondaryClass = 'text-slate-500 dark:text-slate-400';
</script>
